import type { Document } from "@models";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { usePatchDocumentMutation } from "src/hooks/mutations";

type RenameDocumentDialogProps = {
  deviceId: string;
  document: Document;
  onClose: () => void;
  orgId: string;
};

export const RenameDocumentDialog = ({
  deviceId,
  document,
  onClose,
  orgId,
}: RenameDocumentDialogProps) => {
  const patchDocumentMutation = usePatchDocumentMutation();
  const [name, setName] = useState(document.title);
  const [error, setError] = useState<string | null>(null);
  const trimmedName = name.trim();
  const isSaveDisabled =
    patchDocumentMutation.isPending ||
    trimmedName.length === 0 ||
    trimmedName === document.title.trim();

  const closeDialog = () => {
    if (!patchDocumentMutation.isPending) {
      onClose();
    }
  };

  const renameDocument = async () => {
    if (isSaveDisabled) {
      return;
    }

    try {
      await patchDocumentMutation.mutateAsync({
        orgId,
        docId: document.id,
        deviceId,
        data: { title: trimmedName },
      });
      onClose();
    } catch {
      setError("Could not rename document. Please try again.");
    }
  };

  return (
    <Dialog
      aria-labelledby="rename-document-dialog-title"
      fullWidth
      maxWidth="xs"
      onClose={closeDialog}
      open
    >
      <DialogTitle id="rename-document-dialog-title">
        Rename document
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          label="Document name"
          margin="dense"
          onChange={(event) => {
            setName(event.target.value);
            setError(null);
          }}
          value={name}
        />
      </DialogContent>
      <DialogActions>
        <Button
          color="inherit"
          disabled={patchDocumentMutation.isPending}
          onClick={closeDialog}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={isSaveDisabled}
          onClick={() => void renameDocument()}
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
